import { Sparkles } from "lucide-react";
import {
  SiClaude,
  SiGooglegemini,
  SiMeta,
  SiMistralai,
  SiNvidia,
  SiOpenai,
} from "react-icons/si";

export const CLAUDE_ORANGE = "#d97757";

export type ProviderIconProps = { size: number; isDark: boolean };

/** Gradient defs referenced by the Gemini mark. Render once per page. */
export function ProviderIconDefs() {
  return (
    <svg width="0" height="0" className="absolute" aria-hidden="true">
      <defs>
        <linearGradient id="llm-gemini-gradient" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#4285f4" />
          <stop offset="45%" stopColor="#9b72cb" />
          <stop offset="100%" stopColor="#d96570" />
        </linearGradient>
      </defs>
    </svg>
  );
}

export function NewThisMonthIcon({ size }: ProviderIconProps) {
  return <Sparkles size={size} strokeWidth={1.6} color="#8b5cf6" />;
}

export function AnthropicIcon({ size }: ProviderIconProps) {
  return <SiClaude size={size} color={CLAUDE_ORANGE} />;
}

export function OpenAIIcon({ size, isDark }: ProviderIconProps) {
  return <SiOpenai size={size} color={isDark ? "#ffffff" : "#0d0d0d"} />;
}

export function GoogleIcon({ size }: ProviderIconProps) {
  return <SiGooglegemini size={size} fill="url(#llm-gemini-gradient)" />;
}

export function NvidiaIcon({ size }: ProviderIconProps) {
  return <SiNvidia size={size} color="#76b900" />;
}

export function MistralIcon({ size }: ProviderIconProps) {
  return <SiMistralai size={size} color="#fa520f" />;
}

export function MetaIcon({ size }: ProviderIconProps) {
  return <SiMeta size={size} color="#0064e0" />;
}

export function DeepSeekIcon({ size }: ProviderIconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none">
      <path
        d="M2.2 13.2c0-3 2.6-5.4 5.9-5.4h2.6c3.1 0 5.8 1.9 6.8 4.5l2.9-1.9c.5-.35 1.2.05 1.05.6l-.8 2.6.8 2.6c.17.57-.53.97-1.03.62l-2.9-2c-1 2.6-3.7 4.4-6.8 4.4H8.1c-3.3 0-5.9-2.4-5.9-5.4Z"
        fill="#4d6bfe"
      />
      <circle cx="6.6" cy="12.2" r="0.95" fill="#ffffff" />
    </svg>
  );
}

/** Math.sin/cos can differ in their last decimal digits across JS engines,
 * which breaks SSR hydration when the raw result is used as an SVG
 * attribute. Rounding keeps server and client output byte-identical. */
function round(value: number) {
  return Math.round(value * 1000) / 1000;
}

export function ZaiIcon({ size }: ProviderIconProps) {
  const rings = [
    { radius: 3.4, count: 6, dot: 0.95, offset: 0 },
    { radius: 6.6, count: 12, dot: 1, offset: 0.26 },
    { radius: 9.4, count: 18, dot: 0.8, offset: 0.12 },
  ];

  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="#2f6fed">
      <circle cx="12" cy="12" r="1.2" />
      {rings.map(({ radius, count, dot, offset }) =>
        Array.from({ length: count }, (_, i) => {
          const angle = (i / count) * Math.PI * 2 + offset;
          return (
            <circle
              key={`${radius}-${i}`}
              cx={round(12 + radius * Math.cos(angle))}
              cy={round(12 + radius * Math.sin(angle))}
              r={dot}
            />
          );
        }),
      )}
    </svg>
  );
}

export function MoonshotIcon({ size, isDark }: ProviderIconProps) {
  const body = isDark ? "#f2f2f2" : "#0f0f0f";
  const stripe = isDark ? "#0f0f0f" : "#ffffff";

  return (
    <svg viewBox="0 0 24 24" width={size} height={size}>
      <circle cx="12" cy="12" r="11" fill={body} />
      <g
        stroke={stripe}
        strokeWidth="1.15"
        strokeLinecap="round"
        fill="none"
      >
        <path d="M3.2 8.4c4.6 1.2 11.5 1.6 17.3.7" />
        <path d="M2.2 12.4c5 1 12.3 1.2 19.3-.5" />
        <path d="M3.6 16.2c4.5.8 10.7.6 16.1-1" />
        <path d="M6.6 19.6c3.3.4 7.2.1 10.7-1" />
      </g>
    </svg>
  );
}

export function QwenIcon({ size }: ProviderIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="#6d5cff"
      strokeWidth="1.7"
      strokeLinejoin="round"
      strokeLinecap="round"
    >
      <path d="M12 2.4 20.8 7.6v8.8L12 21.6 3.2 16.4V7.6Z" />
      <path d="M8.1 9.3 12 15.2l3.9-5.9" />
    </svg>
  );
}

export function Ai21Icon({ size, isDark }: ProviderIconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size}>
      <circle
        cx="12"
        cy="12"
        r="11"
        fill={isDark ? "rgba(255,255,255,0.14)" : "#e6e6e6"}
      />
      <text
        x="12"
        y="12"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="12"
        fontWeight="500"
        fill={isDark ? "rgba(255,255,255,0.75)" : "#59595e"}
      >
        A
      </text>
    </svg>
  );
}

export function SakanaIcon({ size, isDark }: ProviderIconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size}>
      <circle
        cx="12"
        cy="12"
        r="11"
        fill={isDark ? "rgba(255,255,255,0.14)" : "#e6e6e6"}
      />
      <text
        x="12"
        y="12"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="12"
        fontWeight="500"
        fill={isDark ? "rgba(255,255,255,0.75)" : "#59595e"}
      >
        S
      </text>
    </svg>
  );
}

export function SpaceXaiIcon({ size, isDark }: ProviderIconProps) {
  const stroke = isDark ? "#f2f2f2" : "#1a1a1a";
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none">
      <circle cx="12" cy="12" r="9.5" stroke={stroke} strokeWidth="1.6" />
      <path d="M6 18 18 6" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}
